import { printHtml } from "./print";
import { getCompanyInfo } from "./supplierDocuments";

const formatMoney = (val) => Number(val || 0).toFixed(2) + " €";

const escapeHtml = (str) =>
  String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const getStatusLabel = (status, documentType) => {
  if (documentType === "facture") {
    switch (status) {
      case "pending":
        return "En attente";
      case "delivered":
        return "Livrée";
      case "paid":
        return "Payée";
      case "overdue":
        return "En retard";
      default:
        return status || "En attente";
    }
  } else {
    switch (status) {
      case "draft":
        return "Brouillon";
      case "sent":
        return "Envoyé";
      case "accepted":
        return "Accepté";
      case "rejected":
        return "Refusé";
      default:
        return status || "Brouillon";
    }
  }
};

const getPaymentMethodLabel = (method) => {
  switch (method) {
    case "virement":
      return "Virement bancaire";
    case "cb":
      return "Carte bancaire";
    case "cash":
      return "Espèces";
    case "cheque":
      return "Chèque";
    default:
      return method || "Virement bancaire";
  }
};

const baseStyles = `
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 13px;
  color: #212529;
  line-height: 1.5;
  margin: 0;
  padding: 30px;
  background: #ffffff;
  box-sizing: border-box;
`;

const getDocumentShell = (content) => `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Document B2B</title>
      <style>
        @page {
          size: A4;
          margin: 15mm;
        }
        @media print {
          body {
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
        }
        * {
          box-sizing: border-box;
        }
      </style>
    </head>
    <body style="${baseStyles}">
      ${content}
    </body>
  </html>
`;

const buildHeader = (title, sale, company, client) => {
  const isDevis = sale.documentType === "devis";
  const docRef = sale.documentNumber || sale.id;
  const docDate = formatDate(sale.createdAt);
  const statusLabel = getStatusLabel(sale.status, sale.documentType);
  const paymentLabel = getPaymentMethodLabel(sale.paymentMethod);

  return `
    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 2px solid #0d6efd; padding-bottom: 20px;">
      <div style="width: 50%;">
        <h1 style="margin: 0 0 6px 0; font-size: 24px; color: #0d6efd; text-transform: uppercase; letter-spacing: 0.5px;">
          ${company.companyName || "ALMOUNE"}
        </h1>
        <div style="color: #495057; font-size: 12px; line-height: 1.6;">
          ${company.address ? company.address.replace(/\n/g, "<br/>") + "<br/>" : ""}
          ${company.phone ? "<strong>Tél :</strong> " + company.phone + "<br/>" : ""}
          ${company.email ? "<strong>Email :</strong> " + company.email + "<br/>" : ""}
          ${company.siret ? "<strong>SIRET :</strong> " + company.siret + "<br/>" : ""}
          ${company.tvaIntracom ? "<strong>N° TVA Intra :</strong> " + company.tvaIntracom : ""}
        </div>
      </div>
      <div style="width: 45%; text-align: right;">
        <div style="display: inline-block; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 12px 18px; text-align: right; min-width: 240px;">
          <h2 style="margin: 0 0 8px 0; font-size: 22px; color: #212529; text-transform: uppercase;">
            ${title}
          </h2>
          <div style="font-size: 13px; line-height: 1.6;">
            <div><strong>N° :</strong> <span style="font-family: monospace; font-size: 14px;">${docRef}</span></div>
            <div><strong>Date :</strong> ${docDate}</div>
            ${sale.expectedDate ? `<div><strong>${isDevis ? "Validité" : "Échéance"} :</strong> ${formatDate(sale.expectedDate)}</div>` : ""}
            ${sale.deliveryDate ? `<div><strong>Livraison :</strong> ${formatDate(sale.deliveryDate)}</div>` : ""}
            <div><strong>Statut :</strong> <span style="display: inline-block; padding: 1px 6px; background-color: #e9ecef; border-radius: 3px; font-weight: bold; font-size: 11px;">${statusLabel}</span></div>
            <div><strong>Règlement :</strong> ${paymentLabel}</div>
          </div>
        </div>
      </div>
    </div>

    <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
      <div style="width: 48%;"></div>
      <div style="width: 48%; border: 1px solid #ced4da; border-radius: 4px; padding: 15px; background-color: #ffffff;">
        <div style="font-size: 11px; font-weight: bold; text-transform: uppercase; color: #6c757d; margin-bottom: 6px;">
          Client / Destinataire
        </div>
        <div style="font-size: 15px; font-weight: bold; color: #212529; margin-bottom: 4px;">
          ${client.companyName || client.name || "Client Professionnel"}
        </div>
        <div style="font-size: 12px; color: #495057; line-height: 1.5;">
          ${client.contactName ? "<strong>Contact :</strong> " + client.contactName + "<br/>" : ""}
          ${client.address ? "<strong>Adresse de facturation :</strong><br/>" + client.address.replace(/\n/g, "<br/>") + "<br/>" : ""}
          ${client.deliveryAddress && client.deliveryAddress !== client.address ? "<strong>Adresse de livraison :</strong><br/>" + client.deliveryAddress.replace(/\n/g, "<br/>") + "<br/>" : ""}
          ${client.phone ? "<strong>Tél :</strong> " + client.phone + "<br/>" : ""}
          ${client.email ? "<strong>Email :</strong> " + client.email + "<br/>" : ""}
          ${client.siret ? "<strong>SIRET :</strong> " + client.siret + "<br/>" : ""}
          ${client.tva ? "<strong>N° TVA :</strong> " + client.tva : ""}
        </div>
      </div>
    </div>
  `;
};

const buildItemsTable = (items = []) => {
  const rows = items.map((item, index) => {
    const qty = Number(item.qty ?? item.quantity ?? 0);
    // CRITICAL: Display ONLY salePrice, never unitCost!
    const salePrice = Number(item.salePrice ?? item.sale_price ?? item.price ?? 0);
    const tvaRate = Number(item.tvaRate ?? item.tva_rate ?? 0.055);
    const lineTotalHT = item.lineTotalHT !== undefined ? Number(item.lineTotalHT) : qty * salePrice;
    const bg = index % 2 === 0 ? "#ffffff" : "#fbfbfb";

    return `
      <tr style="background-color: ${bg};">
        <td style="border: 1px solid #dee2e6; padding: 8px 10px; font-size: 12px; font-family: monospace;">${item.productId || "-"}</td>
        <td style="border: 1px solid #dee2e6; padding: 8px 10px; font-size: 12px;"><strong>${escapeHtml(item.productName || item.name || "Article")}</strong></td>
        <td style="border: 1px solid #dee2e6; padding: 8px 10px; text-align: right; font-size: 12px; font-family: monospace;">${qty}</td>
        <td style="border: 1px solid #dee2e6; padding: 8px 10px; text-align: right; font-size: 12px; font-family: monospace;">${formatMoney(salePrice)}</td>
        <td style="border: 1px solid #dee2e6; padding: 8px 10px; text-align: right; font-size: 12px; font-family: monospace;">${(tvaRate * 100).toFixed(1)}%</td>
        <td style="border: 1px solid #dee2e6; padding: 8px 10px; text-align: right; font-size: 12px; font-family: monospace; font-weight: bold;">${formatMoney(lineTotalHT)}</td>
      </tr>
    `;
  }).join("");

  return `
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 25px; font-size: 12px;">
      <thead>
        <tr style="background-color: #f1f3f5; color: #212529;">
          <th style="border: 1px solid #dee2e6; padding: 9px 10px; text-align: left; width: 15%;">Réf</th>
          <th style="border: 1px solid #dee2e6; padding: 9px 10px; text-align: left; width: 40%;">Désignation</th>
          <th style="border: 1px solid #dee2e6; padding: 9px 10px; text-align: right; width: 10%;">Qté</th>
          <th style="border: 1px solid #dee2e6; padding: 9px 10px; text-align: right; width: 15%;">P.U. HT</th>
          <th style="border: 1px solid #dee2e6; padding: 9px 10px; text-align: right; width: 10%;">TVA</th>
          <th style="border: 1px solid #dee2e6; padding: 9px 10px; text-align: right; width: 10%;">Total HT</th>
        </tr>
      </thead>
      <tbody>
        ${rows.length > 0 ? rows : `<tr><td colspan="6" style="border: 1px solid #dee2e6; padding: 15px; text-align: center; color: #6c757d;">Aucun article</td></tr>`}
      </tbody>
    </table>
  `;
};

const buildTotals = (items = [], sale = {}) => {
  let totalHT = 0;
  const tvaBreakdown = {};

  items.forEach((item) => {
    const qty = Number(item.qty ?? item.quantity ?? 0);
    const salePrice = Number(item.salePrice ?? item.sale_price ?? item.price ?? 0);
    const lineTotalHT = item.lineTotalHT !== undefined ? Number(item.lineTotalHT) : qty * salePrice;
    totalHT += lineTotalHT;

    const rate = Number(item.tvaRate ?? item.tva_rate ?? 0.055);
    const key = rate.toFixed(3);
    if (!tvaBreakdown[key]) {
      tvaBreakdown[key] = { rate, baseHT: 0, amountTVA: 0 };
    }
    tvaBreakdown[key].baseHT += lineTotalHT;
    tvaBreakdown[key].amountTVA += lineTotalHT * rate;
  });

  let computedTVA = 0;
  const tvaRows = Object.values(tvaBreakdown)
    .sort((a, b) => a.rate - b.rate)
    .map((entry) => {
      computedTVA += entry.amountTVA;
      return `
        <tr>
          <td style="padding: 6px 10px; text-align: left; border: 1px solid #dee2e6; font-size: 12px;">
            TVA ${(entry.rate * 100).toFixed(1)}% (sur ${formatMoney(entry.baseHT)})
          </td>
          <td style="padding: 6px 10px; text-align: right; border: 1px solid #dee2e6; font-family: monospace; font-size: 12px;">
            ${formatMoney(entry.amountTVA)}
          </td>
        </tr>
      `;
    })
    .join("");

  const finalTotalHT = sale.totalHT !== undefined ? Number(sale.totalHT) : totalHT;
  const finalTotalTVA = sale.totalTVA !== undefined ? Number(sale.totalTVA) : computedTVA;
  const finalTotalTTC = sale.totalTTC !== undefined ? Number(sale.totalTTC) : (finalTotalHT + finalTotalTVA);

  return `
    <div class="page-break-inside-avoid" style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
      <table style="width: 45%; border-collapse: collapse; border: 1px solid #dee2e6;">
        <tr>
          <td style="padding: 7px 10px; text-align: left; border: 1px solid #dee2e6; font-size: 12px; background-color: #f8f9fa;">
            <strong>Total HT</strong>
          </td>
          <td style="padding: 7px 10px; text-align: right; border: 1px solid #dee2e6; font-family: monospace; font-size: 13px; font-weight: bold;">
            ${formatMoney(finalTotalHT)}
          </td>
        </tr>
        ${tvaRows}
        <tr style="background-color: #e7f1ff; color: #0c63e4;">
          <td style="padding: 9px 10px; text-align: left; border: 1px solid #b6d4fe; font-size: 14px;">
            <strong>TOTAL TTC</strong>
          </td>
          <td style="padding: 9px 10px; text-align: right; border: 1px solid #b6d4fe; font-family: monospace; font-size: 15px; font-weight: bold;">
            ${formatMoney(finalTotalTTC)}
          </td>
        </tr>
      </table>
    </div>
  `;
};

const buildFooter = (sale = {}) => {
  const isDevis = sale.documentType === "devis";
  const notes = sale.notes || "";

  return `
    <div class="page-break-inside-avoid" style="margin-top: 30px; font-size: 11px; color: #495057; border-top: 1px solid #dee2e6; padding-top: 15px;">
      ${notes ? `
        <div style="margin-bottom: 15px; background-color: #f8f9fa; padding: 10px; border-radius: 4px; border-left: 3px solid #0d6efd;">
          <strong>Notes & Instructions :</strong><br/>
          ${notes.replace(/\n/g, "<br/>")}
        </div>
      ` : ""}

      ${isDevis ? `
        <div style="display: flex; justify-content: space-between; margin-top: 20px;">
          <div style="width: 55%; line-height: 1.5;">
            <p style="margin: 0 0 5px 0;"><strong>Conditions :</strong> Devis valable 30 jours à compter de la date d'émission.</p>
            <p style="margin: 0;">Pour valider ce devis, veuillez nous le retourner signé avec la mention manuscrite <em>"Bon pour accord"</em>.</p>
          </div>
          <div style="width: 40%; border: 1px dashed #6c757d; border-radius: 4px; padding: 15px; min-height: 90px; text-align: left;">
            <strong style="font-size: 11px;">Bon pour accord</strong><br/>
            <span style="font-size: 10px; color: #6c757d;">Date et Signature du client :</span>
          </div>
        </div>
      ` : `
        <div style="line-height: 1.5; color: #6c757d; font-size: 10px;">
          <p style="margin: 0 0 4px 0;">
            <strong>Conditions de règlement :</strong> Paiement par ${getPaymentMethodLabel(sale.paymentMethod)}.
          </p>
          <p style="margin: 0 0 4px 0;">
            En cas de retard de paiement, une indemnité forfaitaire pour frais de recouvrement de 40 € sera due de plein droit (Art. D. 441-5 du Code de commerce), ainsi que des pénalités de retard équivalentes à 3 fois le taux d'intérêt légal en vigueur. Pas d'escompte accordé pour paiement anticipé.
          </p>
        </div>
      `}
    </div>
  `;
};

/**
 * Builds standard French HTML layout for Devis and Factures B2B.
 *
 * @param {object} sale - The B2B sale object
 * @param {object} client - The B2B client object
 * @param {object} companyInfo - Company/Seller details
 * @returns {string} Clean HTML string ready for rendering or printing
 */
export const buildB2BDocumentHtml = (sale = {}, client = {}, companyInfo = null) => {
  const company = companyInfo || getCompanyInfo();
  const safeClient = client || {};
  const isDevis = sale.documentType === "devis";
  const title = isDevis ? "DEVIS" : "FACTURE";

  const content = `
    ${buildHeader(title, sale, company, safeClient)}
    ${buildItemsTable(sale.items || [])}
    ${buildTotals(sale.items || [], sale)}
    ${buildFooter(sale)}
  `;

  return getDocumentShell(content);
};

/**
 * Prints a B2B Document (Devis or Facture) via a hidden iframe.
 *
 * @param {object} sale - The B2B sale object
 * @param {object} client - The B2B client object
 * @param {object} companyInfo - Company/Seller details
 * @returns {boolean} true if print was successfully initiated
 */
export const printB2BDocument = (sale = {}, client = {}, companyInfo = null) => {
  const isDevis = sale.documentType === "devis";
  const docTypeLabel = isDevis ? "Devis" : "Facture";
  const docNumber = sale.documentNumber || sale.id || "document";
  
  const html = buildB2BDocumentHtml(sale, client, companyInfo);
  return printHtml(html, {
    title: `${docTypeLabel} ${docNumber}`,
  });
};


export const buildBonDeLivraisonHtml = (sale = {}, client = {}, companyInfo = null) => {
  const company = companyInfo || getCompanyInfo();
  const safeClient = client || {};
  const docNumber = sale.documentNumber || sale.id || 'document';
  
  const itemsHtml = (sale.items || []).map(item => `
    <tr>
      <td style="border: 1px solid #dee2e6; padding: 10px; text-align: left; font-size: 13px;">${item.productId || '-'}</td>
      <td style="border: 1px solid #dee2e6; padding: 10px; text-align: left; font-size: 13px;">${escapeHtml(item.productName || 'Produit')}</td>
      <td style="border: 1px solid #dee2e6; padding: 10px; text-align: right; font-size: 13px; font-weight: bold;">${item.quantity || item.qty || 1}</td>
    </tr>
  `).join('');

  const content = `
    <div style="margin-bottom: 30px; display: flex; justify-content: space-between;">
      <div>
        <h2 style="margin: 0; color: #212529;">BON DE LIVRAISON</h2>
        <p style="margin: 5px 0; font-size: 14px; color: #6c757d;">N° ${docNumber}</p>
        <p style="margin: 5px 0; font-size: 14px; color: #6c757d;">Date : ${new Date(sale.createdAt || sale.date).toLocaleDateString('fr-FR')}</p>
      </div>
      <div style="text-align: right;">
        <h3 style="margin: 0;">${company.companyName || 'ALMOUNE'}</h3>
      </div>
    </div>
    
    <div style="margin-bottom: 30px; display: flex; justify-content: space-between; gap: 20px;">
      <div style="flex: 1; border: 1px solid #dee2e6; border-radius: 6px; padding: 15px;">
        <h4 style="margin-top: 0; color: #495057;">Adresse de Livraison</h4>
        <strong>${safeClient.companyName || safeClient.name || 'Client'}</strong><br/>
        ${safeClient.deliveryAddress || safeClient.address || ''}<br/>
        ${safeClient.phone ? `Tél : ${safeClient.phone}<br/>` : ''}
        ${safeClient.contactName ? `Contact : ${safeClient.contactName}` : ''}
      </div>
    </div>

    <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
      <thead>
        <tr style="background-color: #f1f3f5;">
          <th style="border: 1px solid #dee2e6; padding: 10px; text-align: left;">Réf</th>
          <th style="border: 1px solid #dee2e6; padding: 10px; text-align: left;">Désignation</th>
          <th style="border: 1px solid #dee2e6; padding: 10px; text-align: right;">Quantité Livrée</th>
        </tr>
      </thead>
      <tbody>
        ${itemsHtml}
      </tbody>
    </table>

    <div style="margin-top: 40px; display: flex; justify-content: space-between;">
      <div style="width: 45%; border: 1px dashed #ced4da; border-radius: 4px; padding: 15px; min-height: 100px;">
        <strong>Visa Chauffeur / Livreur :</strong>
      </div>
      <div style="width: 45%; border: 1px dashed #ced4da; border-radius: 4px; padding: 15px; min-height: 100px;">
        <strong>Visa Client & Cachet :</strong><br/>
        <span style="font-size: 11px; color: #6c757d;">Reçu en bon état le : __/__/____</span>
      </div>
    </div>
  `;

  return getDocumentShell(content);
};

export const buildEtiquetteExpeditionHtml = (sale = {}, client = {}, companyInfo = null) => {
  const company = companyInfo || getCompanyInfo();
  const safeClient = client || {};
  const docNumber = sale.documentNumber || sale.id || 'document';
  
  return `
    <html>
      <head>
        <title>Étiquette d'expédition - ${docNumber}</title>
        <style>
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; margin: 0; padding: 20px; }
          .label-container { 
            width: 100mm; 
            height: 150mm; 
            border: 2px solid #000; 
            padding: 15px; 
            box-sizing: border-box; 
            display: flex; 
            flex-direction: column;
            margin: 0 auto;
          }
          .sender { border-bottom: 1px solid #000; padding-bottom: 10px; margin-bottom: 20px; font-size: 12px; }
          .recipient { flex-grow: 1; font-size: 16px; line-height: 1.5; padding-left: 20px; }
          .recipient h2 { margin: 0 0 10px 0; font-size: 22px; }
          .ref { border-top: 1px solid #000; padding-top: 10px; margin-top: 10px; font-size: 14px; font-weight: bold; text-align: center; }
        </style>
      </head>
      <body>
        <div class="label-container">
          <div class="sender">
            <strong>EXPÉDITEUR :</strong><br/>
            ${company.companyName || 'ALMOUNE'}<br/>
            ${company.address || ''}<br/>
            ${company.phone || ''}
          </div>
          <div class="recipient">
            <strong>DESTINATAIRE :</strong><br/><br/>
            <h2>${safeClient.companyName || safeClient.name || 'Client'}</h2>
            ${safeClient.deliveryAddress || safeClient.address || ''}<br/><br/>
            ${safeClient.phone ? `Tél : ${safeClient.phone}<br/>` : ''}
            ${safeClient.contactName ? `Attn : ${safeClient.contactName}` : ''}
          </div>
          <div class="ref">
            Réf Commande : ${docNumber}<br/>
            Date : ${new Date(sale.createdAt || sale.date).toLocaleDateString('fr-FR')}
          </div>
        </div>
        <script>
          window.onload = () => window.print();
        </script>
      </body>
    </html>
  `;
};

export const printBonDeLivraison = (sale, client, companyInfo) => {
  const html = buildBonDeLivraisonHtml(sale, client, companyInfo);
  return printHtml(html, { title: `BL ${sale.documentNumber || sale.id}` });
};

export const printEtiquette = (sale, client, companyInfo) => {
  const html = buildEtiquetteExpeditionHtml(sale, client, companyInfo);
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();
  setTimeout(() => {
    document.body.removeChild(iframe);
  }, 5000);
};
